/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Analytics › Sessions (contract section 5): how many sessions, how many were engaged (10 s or longer, or 2+ screens), the
 * median length, the length buckets, and when people use Gyro by hour (America/Los_Angeles) and by weekday, from
 * GET /v1/admin/usage/sessions with the lens.
 */
import { Clock } from 'lucide-react';
import { api } from '../../lib/api';
import { durationText, engagedShare, hourLabel, normalizeSessions, peakIndex, weekdayName } from '../../lib/usage';
import { EmptyState, Grid, Kpi, MeterRow, Panel, Sparkline, Stack } from '../../ui';
import { count, percent } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { UsageError, type SectionProps } from './parts';

export function UsageSessions({ lens, query, lensKey }: SectionProps) {
  const { load, reload, reloading } = useLoad(() => api.usageSessions(query).then(normalizeSessions), `sessions|${lensKey}`);
  const data = load.state === 'ready' ? load.data : null;
  const loading = load.state === 'loading';
  if (load.state === 'failed') return <Panel title="Sessions"><UsageError title="Sessions could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /></Panel>;

  const peakHour = data ? peakIndex(data.byHour) : null;
  const peakDay = data ? peakIndex(data.byWeekday) : null;
  const bucketTop = data ? Math.max(1, ...data.buckets.map((bucket) => bucket.count)) : 1;
  const weekdayTop = data ? Math.max(1, ...data.byWeekday) : 1;
  const empty = data !== null && data.count === 0;

  return (
    <Stack>
      <Panel title="Sessions" description={`Last ${lens.window} days. A session starts cold, on a user change, or after 30 minutes away.`}>
        <Grid cols={4} label="Sessions">
          <Kpi icon={Clock} label="Sessions" loading={loading} value={data ? count(data.count) : '—'} />
          <Kpi label="Engaged" loading={loading} value={data ? percent(engagedShare(data), 1) : '—'} sub={data ? `${count(data.engaged)} sessions 10 s or longer, or 2+ screens` : undefined} />
          <Kpi label="Median length" loading={loading} value={data && data.count > 0 ? durationText(data.medianMs) : '—'} sub={data && data.count === 0 ? 'no sessions yet' : undefined} />
          <Kpi label="Busiest hour" loading={loading} value={peakHour === null ? '—' : hourLabel(peakHour)} sub={peakHour === null ? 'nothing yet' : `${count(data?.byHour[peakHour])} sessions, Pacific time`} />
        </Grid>
      </Panel>
      {empty ? (
        <Panel title="When people use Gyro">
          <EmptyState compact icon={Clock} title="No sessions in this window">Sessions appear here as people use Gyro; data starts at the client release, and the lens above may be leaving people out.</EmptyState>
        </Panel>
      ) : (
        <Grid cols={2}>
          <Panel title="Session length" description="Sessions by how long they lasted">
            {loading ? <EmptyState compact title="Loading" /> : data && data.buckets.length ? (
              <div className="usage-bars">
                {data.buckets.map((bucket) => <MeterRow key={bucket.label} label={bucket.label} figure={count(bucket.count)} value={bucket.count / bucketTop} meterLabel={`${bucket.label}: ${count(bucket.count)} sessions`} />)}
              </div>
            ) : <EmptyState compact title="No length buckets">The service answered no buckets for this window.</EmptyState>}
          </Panel>
          <Stack>
            <Panel title="By hour" description="Sessions started in each hour of the day, Pacific time">
              {loading ? <EmptyState compact title="Loading" /> : data ? (
                <div className="ui-cell-stack">
                  <Sparkline values={data.byHour} label="Sessions by hour of the day, 00:00 to 23:00" />
                  <span className="ui-cell-sub">{peakHour === null ? 'No sessions yet' : `Busiest at ${hourLabel(peakHour)}`}</span>
                </div>
              ) : null}
            </Panel>
            <Panel title="By weekday" description="Sessions started on each day of the week">
              {loading ? <EmptyState compact title="Loading" /> : data ? (
                <div className="usage-bars">
                  {data.byWeekday.map((value, index) => <MeterRow key={index} label={weekdayName(index)} figure={count(value)} value={value / weekdayTop} meterLabel={`${weekdayName(index)}: ${count(value)} sessions`} tone={index === peakDay ? 'accent' : 'data'} />)}
                </div>
              ) : null}
            </Panel>
          </Stack>
        </Grid>
      )}
    </Stack>
  );
}
